import { notification, theme } from 'antd';
import { toJS } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useCallback, useEffect, useState } from 'react';
import { GrispiEvents } from '../../EventAndActionNames';
import grispiApi from '../../apis/grispiApi';
import { callPluginStatusToCallClientStatusMap } from '../../model/types';
import { usePluginStore } from '../../store/RootStoreContext';
import { getCurrentTicketKey, getCurrentUserEmail, getTenantId } from '../../utils/accountHelper';
import { hasDebugHash, isSSR } from '../../utils/helpers';
import getStore from '../../utils/store/getStore';
import { getTabId } from '../../utils/tabId';
import uuid from '../../utils/uuid';
import EmptyPlugin from '../EmptyPlugin';
import { UI_STATES } from './CallApp';
import { PluginContext, PluginDefinition, PluginType, PluginUser } from './types';

const PLUGIN_USAGE_INDICATOR_LOCAL_STORAGE_KEY_PREFIX = 'grispi:';
// const LOG_LABEL = 'PluginApp';

// TODO destan: in order to implement plugin reloading (GRISPI-96), we need to reset 'initCalled' variable because the plugin needs
//to re-send the init message after the reload.
// GRISPI-96 - As an agent I can reload a plugin if it misbehaves.
let initCalled = false;

function makeMessage(type, data) {
    return { type, data };
}

function generatePluginUsageIndicatorLocalStorageKey(pluginId: string): string {
    return PLUGIN_USAGE_INDICATOR_LOCAL_STORAGE_KEY_PREFIX + pluginId;
}

function isPluginInUseInOtherTab(pluginId: string): boolean {
    const currentTabId = getTabId();
    const pluginIdKey = generatePluginUsageIndicatorLocalStorageKey(pluginId);
    const tabIdOfPlugin = localStorage.getItem(pluginIdKey);
    return typeof tabIdOfPlugin === 'string' && tabIdOfPlugin.length > 0 && tabIdOfPlugin != currentTabId;
}

function removePluginUsageIndicatorOfCurrentTab(pluginId: string) {
    const currentTabId = getTabId();
    const pluginIdKey = generatePluginUsageIndicatorLocalStorageKey(pluginId);
    const tabIdOfPlugin = localStorage.getItem(pluginIdKey);
    if (currentTabId == tabIdOfPlugin) localStorage.removeItem(pluginIdKey);
}

function setPluginInUseIndicatorForCurrentTab(pluginId: string) {
    const currentTabId = getTabId();
    const pluginIdKey = generatePluginUsageIndicatorLocalStorageKey(pluginId);
    localStorage.setItem(pluginIdKey, currentTabId);
}

//<editor-fold desc="messageHandler()" defaultstate="collapsed">
// Handles messages coming from the iFrame
async function messageHandler(e: any, iFrameEl: HTMLIFrameElement, pluginDefinition: PluginDefinition) {
    // Following log is too noisy for prod env. because it catches all unrelated messages (ex. react-devtools-bridge)
    //so only use it in dev env.

    if (!iFrameEl) return;

    const targetOrigin = pluginDefinition?.manifest.src;
    try {
        if (JSON.parse(e.data).sender === 'AloTech') {
            iFrameEl.contentWindow.postMessage(e.data, targetOrigin);
        }
        // eslint-disable-next-line no-empty
    } catch (e) {}

    if (iframeAuth !== e.data.iframeAuth) {
        // Let's keep below line to hint new developers what to debug
        return;
    }

    if (e.origin !== targetOrigin) {
        return;
    }

    if (!e.data) {
        return;
    }

    if (!e.data.type) {
        return;
    }

    if (hasDebugHash()) {
        notification.open({
            message: 'Message from an plugin.',
            description: JSON.stringify(e.data, null, 2),
            placement: 'bottomRight'
        });
    }

    if (e.data.type === GrispiEvents.REQUEST_INIT) {
        if (initCalled) {
            // console.error(
            //     LOG_LABEL,
            //     `'${pluginDefinition?.pluginId}' called '${GrispiEvents.REQUEST_INIT}' more than once!`
            // );
        }
        const context = await getContext(pluginDefinition);

        // getContext has api calls and it may fail. Then we may need to retry initializing the plugin.
        //In order to be able to do so in such case we won't set initCalled as true until getContext succeeds.
        initCalled = true;

        getStore().PluginStore.setPluginIsInitializing(pluginDefinition?.pluginId, pluginDefinition?.pluginType, true); // to make AgentStatusDropdown to show loading indicator and to prevent user from changing AgentStatus

        const data = {
            settings: toJS(pluginDefinition).settings,
            context: context
        };

        const message = makeMessage(GrispiEvents.RESPONSE_INIT, data);
        iFrameEl?.contentWindow?.postMessage(message, targetOrigin);
        return;
    }

    if (e.data.type === GrispiEvents.REQUEST_CURRENT_TICKET) {
        const message = makeMessage(GrispiEvents.RESPONSE_CURRENT_TICKET, { ticketKey: getCurrentTicketKey() });
        iFrameEl.contentWindow.postMessage(message, targetOrigin);
        return;
    }

    // Unhandled messages are dispatched (redirected) to the iFrame element so that specific plugin components (i.e. CallApp) can listen and act on them.

    const event = new CustomEvent(e.data.type, { detail: e.data.data });
    iFrameEl.dispatchEvent(event);
}
//</editor-fold>

async function getPluginToken(pluginId: string) {
    const path = `/apps/${pluginId}/login`;
    const response = await grispiApi.post(path);
    return response.data.access_token;
}

//<editor-fold desc="getContext()" defaultstate="collapsed">
async function getContext(pluginDefinition: PluginDefinition): Promise<PluginContext> {
    const token = await getPluginToken(pluginDefinition.pluginId);
    const ticket = toJS(getStore().ActiveTicketStore.activeTicket);
    const ticketRequester = ticket?.fieldMap['ts.requester'].value as Record<any, any>;
    const requester: PluginUser = ticketRequester
        ? {
              id: ticketRequester.id,
              fullName: ticketRequester.fullName,
              email: ticketRequester.email,
              phone: ticketRequester.phone
          }
        : null;

    const context: PluginContext = {
        username: getCurrentUserEmail(),
        tenantId: getTenantId(),
        ticketKey: ticket?.key,
        ticket: ticket,
        agent: getStore().CurrentUserStore.toPluginUser(),
        requester: requester,
        token: token
    };

    if (pluginDefinition.pluginType === PluginType.CALL) {
        context.initialStatus = callPluginStatusToCallClientStatusMap[getStore().PluginStore.currentAgentStatus?.call];
    }

    return context;
}
//</editor-fold>

const origin = typeof location === 'object' ? new URL(location.href).origin : '';

const iframeAuth = uuid();

type PluginErrorCallbackFunction = (string) => void;

/**
 * This component represents a plugin's common skeleton.
 * This is the component that renders the plugin's iframe.
 * Every specific plugin component implementation needs to render this component as its child.
 */

// https://stackoverflow.com/questions/70568283/why-mobx-observer-is-not-triggering-re-render-on-react-recurrent-component
const PluginApp = ({
    pluginId,
    errorCallback,
    popoverVisible //FIXME generalize this: PluginApp is a generic component. Not every plugin will be rendered in a popover.
}: {
    pluginId: string;
    errorCallback: PluginErrorCallbackFunction;
    popoverVisible: boolean;
}): JSX.Element => {
    const {
        token: { zIndexPopupBase }
    } = theme.useToken();

    const [loadingTime, setLoadingTime] = useState(new Date().getTime());

    const { pluginDefinitions } = usePluginStore();
    const pluginDefinition = pluginDefinitions ? pluginDefinitions[pluginId] : null;

    const forceLoad = useCallback(() => {
        setLoadingTime(new Date().getTime());
        setPluginInUseIndicatorForCurrentTab(pluginDefinition?.pluginId);
    }, [pluginDefinition?.pluginId]);

    function callErrorCallback() {
        if (typeof errorCallback === 'function') {
            errorCallback(UI_STATES.initializingError);
        }
    }

    const getIframeDomEl = useCallback(() => {
        return document.getElementById(pluginDefinition?.pluginId) as HTMLIFrameElement;
    }, [pluginDefinition?.pluginId]);

    const notReadyToRenderPlugin = isSSR() || !pluginDefinition;

    const srcMissing =
        typeof pluginDefinition?.manifest?.src !== 'string' || pluginDefinition?.manifest?.src?.trim().length === 0;
    const uiDefinitionMissing = typeof pluginDefinition?.manifest?.uiDefinition?.height !== 'number';
    const misConfiguration = srcMissing || uiDefinitionMissing;

    // Render the iframe and attach event listeners
    useEffect(() => {
        if (isPluginInUseInOtherTab(pluginDefinition?.pluginId)) {
            callErrorCallback();
            return;
        }

        if (notReadyToRenderPlugin || misConfiguration) {
            callErrorCallback();
            return;
        }

        setPluginInUseIndicatorForCurrentTab(pluginDefinition?.pluginId);

        // Keeping the existing hash parameters in iframe url from pluginDefinition if any
        const iFrameSourceUrl = new URL(pluginDefinition?.manifest?.src);
        const hash = iFrameSourceUrl.hash;
        const hashParameters = new URLSearchParams(hash.substring(1));
        hashParameters.set('origin', origin);
        hashParameters.set('pluginId', pluginDefinition?.pluginId);
        hashParameters.set('iframeAuth', iframeAuth);
        iFrameSourceUrl.hash = hashParameters.toString();
        const iFrameSource = iFrameSourceUrl.toString();

        if (
            !document.getElementById(pluginDefinition?.pluginId) &&
            pluginDefinition?.pluginId &&
            pluginDefinition?.manifest?.uiDefinition?.height &&
            iFrameSource
        ) {
            document.body.insertAdjacentHTML(
                'beforeend',
                `<iframe
                        id='${pluginDefinition?.pluginId}'
                        style="border: 0; position: absolute; top: 80px; right: 142px; z-index: ${
                            zIndexPopupBase + 30 + 1 // zIndexPopupBase + 30 (default popover z-index) + 1 (place over popover)
                        }"
                        height="${+pluginDefinition?.manifest?.uiDefinition?.height + 10 ?? 'auto'}"
                        src="${iFrameSource}"
                        allow="autoplay;microphone 'src';"
			                />`
                // see https://developer.mozilla.org/en-US/docs/Web/HTTP/Headers/Feature-Policy#syntax for 'src' value
            );

            const iframeDomEl = document.getElementById(pluginDefinition?.pluginId) as HTMLIFrameElement;

            // Handles messages from the iframe
            window.addEventListener('message', (msg: MessageEvent) =>
                messageHandler(msg, iframeDomEl, pluginDefinition)
            );

            // Redirects messages coming from this various GrispiUI components to the iframe's content window
            window.addEventListener(GrispiEvents.MESSAGE_TO_PLUGIN, (e: CustomEvent) => {
                const message = e.detail;
                if (message.pluginId !== pluginId) {
                    // When there are multiple plugins "message.pluginId !== pluginId" is a common and expected state.
                    return;
                }
                iframeDomEl?.contentWindow.postMessage(message.data, pluginDefinition?.manifest.src);
            });

            // Handles internal Grispi -> Grispi (Plugin's React components) events
            // Events will be dispatched (redirected) to iframe dom element itself (not its content window) so that
            // plugin's React component (ex. CallApp) can handle the event in a plugin specific way.
            window.addEventListener(GrispiEvents.SHOW_PLUGIN_EVENT, (e: CustomEvent) => {
                const targetPluginId = e.detail.pluginId;
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                if (targetPluginId === pluginId) iframeDomEl.dispatchEvent(new e.constructor(e.type, e)); //https://stackoverflow.com/a/20541207/878361
            });

            window.addEventListener('unload', function () {
                removePluginUsageIndicatorOfCurrentTab(pluginDefinition?.pluginId);
            });
        }
    }, [
        loadingTime,
        misConfiguration,
        notReadyToRenderPlugin,
        pluginDefinition?.pluginId,
        pluginDefinition?.manifest?.src,
        pluginDefinition?.manifest?.uiDefinition?.height,
        zIndexPopupBase
    ]);

    // Show/Hide iframe in sync with iframe's container
    useEffect(() => {
        const iframeEl = getIframeDomEl();
        iframeEl && (iframeEl.style.display = popoverVisible ? 'block' : 'none');
    }, [popoverVisible]);

    if (notReadyToRenderPlugin) {
        return <EmptyPlugin pluginId={pluginId} pluginName={pluginDefinition?.name} />;
    }

    if (misConfiguration) {
        // const message = `${srcMissing ? 'src' : 'ui definition'} is missing!`;

        return <EmptyPlugin pluginId={pluginId} pluginName={pluginDefinition?.name} />;
    }

    const isPluginSingleton = pluginDefinition.pluginType === PluginType.CALL || pluginDefinition.manifest.singleton;
    if (isPluginSingleton && isPluginInUseInOtherTab(pluginDefinition.pluginId)) {
        return (
            <>
                <div>Bu uygulama başka bir sekmede zaten kullanılıyor.</div>
                <button onClick={forceLoad}>Burada kullan</button>
                <p>
                    <em>
                        Bu işlem uygulamanın yanlış çalışmasına neden olabilir.
                        <br />
                        Başka bir sekmede çalışmadığından eminseniz kullanınız.
                    </em>
                </p>
            </>
        );
    }

    return (
        <div
            id={pluginDefinition.pluginId + 'Container'}
            style={{
                height: +pluginDefinition?.manifest?.uiDefinition?.height + 10 ?? 480,
                width: 300
            }}
        ></div>
    );
};

export default observer(PluginApp);
